import { FormikErrors } from "formik";
import { isUserGeneratedKey } from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/autoGenerateKey";
import * as AF from "src/types/formTemplate";
import { z } from "zod";
import { SchoolItem } from "../../types";
import * as QuestionType from "../QuestionType";

const DisabledEligibilitySchema = z.object({
  type: z.literal("disabled"),
  schoolIds: z.array(z.string()).optional(),
});
export const EligibilityExclusionSchema = z.object({
  type: z.literal("excludes"),
  schoolIds: z
    .array(z.string(), { message: "Exclude at least 1 school" })
    .min(1, "Exclude at least 1 school"),
});
export type EligibilityExclusion = z.infer<typeof EligibilityExclusionSchema>;
export const EligibilitySchema = z.discriminatedUnion("type", [
  DisabledEligibilitySchema,
  EligibilityExclusionSchema,
]);
export type EligibilityFormikType = z.infer<typeof EligibilitySchema>;
const AnswerOptionSchema = z.object({
  id: z.string(),
  label: z.string().trim().min(1, "This field is required"),
  value: z.string().optional(), // make optional to support Grades
  userGeneratedValue: z.boolean(),
  eligibility: EligibilitySchema.optional(),
  isArchived: z.boolean().optional(),
});
export type AnswerOptionFormikType = z.infer<typeof AnswerOptionSchema>;

export const MAX_OPTIONS = 500;

export const FormikSchema = z
  .object({
    options: z
      .array(AnswerOptionSchema)
      .max(MAX_OPTIONS, `Exceeded maximum limit of ${MAX_OPTIONS} answers`),
  })
  .and(QuestionType.FormikSchema)
  .superRefine(({ options, type }, ctx) => {
    if (type !== "SingleSelect" && type !== "MultiSelect") {
      return;
    }

    if (options.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["options"],
        message: "At least 1 answer is required",
      });
    }

    // See comment below before changing the order of this validation
    options.forEach((option, index) => {
      if (option.value === undefined || option.value.trim() === "") {
        ctx.addIssue({
          path: ["options", index, "value"],
          code: z.ZodIssueCode.custom,
          message: "This field is required",
        });
      }
    });

    // This validation has to be evaluated after validating over options since the issue path will collide with their path.
    // e.g. {"options": {"0": {"label": "This field is required"}}} vs {"options": "Exceeded maximum limit of 500 answers"}
    if (options.length > MAX_OPTIONS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["options"],
        message: "Exceeded maximum limit of 500 answers",
      });
    }
  });

export type FormikType = z.infer<typeof FormikSchema>;

export function isEnable(
  eligibility: EligibilityFormikType | undefined
): eligibility is EligibilityExclusion {
  return eligibility?.type === "excludes";
}

export function toFormValue(question: AF.V2.Question): FormikType {
  const options: AnswerOptionFormikType[] =
    "options" in question ? question.options.map(toAnswerOptionFormValue) : [];

  return {
    ...QuestionType.toFormValue(question),
    options,
  };
}

function toAnswerOptionFormValue(option: AF.V2.Option): AnswerOptionFormikType {
  const userGeneratedValue = isUserGeneratedKey(option.value, option.label);

  const formValue: AnswerOptionFormikType = {
    id: option.id,
    label: option.label,
    value: option.value ?? "",
    userGeneratedValue,
    isArchived: option.isArchived,
  };

  switch (option.eligibilityFilter) {
    case undefined:
    case "NotApplicable":
      return formValue;

    case "NotEligible":
      return {
        ...formValue,
        eligibility: {
          type: "excludes",
          schoolIds: option.notEligibleSchoolIds,
        },
      };

    default:
      const _exhaustiveCheck: never = option;
      throw new Error(`Unhandled eligibility type: ${option}`);
  }
}

export function validateForm(
  schools: SchoolItem[] | null,
  values: FormikType
): FormikErrors<FormikType> {
  // FormikErrors type is annoying to work with since each field can be string | string[] | FormikErrors, so we need to jump through a lot of hoops to make it somewhat "type safe".
  // I don't think it's worth it, so I'm just going to use any for it. - Abadi
  let errors: any = {};

  // Only validate for SingleSelect and MultiSelect questions
  if (values.type !== "SingleSelect" && values.type !== "MultiSelect") {
    return {};
  }

  // Validate each option's eligibility
  values.options.forEach((option, index) => {
    // Skip if eligibility is disabled or undefined
    if (!option.eligibility || option.eligibility.type === "disabled") {
      return;
    }

    // For "excludes" type, validate school IDs
    if (
      option.eligibility.type === "excludes" &&
      option.eligibility.schoolIds
    ) {
      if (schools === null) {
        errors = {
          ...errors,
          options: {
            ...errors?.options,
            [index]: {
              ...errors?.options?.[index],
              eligibility: {
                ...errors?.options?.[index]?.eligibility,
                schoolIds:
                  "Unable to validate schools eligibility, missing schools data",
              },
            },
          },
        };

        return;
      }

      // Check if all school IDs in the exclusion list are valid
      const invalidSchoolIds = option.eligibility.schoolIds.filter(
        (schoolId) => !schools.find((school) => school.id === schoolId)
      );

      if (invalidSchoolIds.length > 0) {
        errors = {
          ...errors,
          options: {
            ...errors?.options,
            [index]: {
              ...errors?.options?.[index],
              eligibility: {
                ...errors?.options?.[index]?.eligibility,
                schoolIds:
                  "One or more selected schools are no longer available",
              },
            },
          },
        };
      }
    }
  });

  return errors;
}
